export type CyanTakeoverIntent =
  | {
      type: "NONE";
    }
  | {
      type: "PREPARE";

      objective: string;
    }
  | {
      type: "ACTIVATE";
    }
  | {
      type: "REFRESH";
    }
  | {
      type: "APPEND";

      objective: string;
    };

function normalized(value: string) {
  return value.trim().toLowerCase();
}

export function isNaturalOperationalMission(message: string): boolean {
  const text = message
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

  if (!text || text.length < 3) {
    return false;
  }

  const ordinaryConversation = [
    /^(oi|ola|hello|hi|hey)\b/,
    /^(obrigado|obrigada|thanks|thank you)\b/,
    /^(quem e voce|who are you)\??$/,
    /^(como voce esta|how are you)\??$/,
  ];

  if (ordinaryConversation.some((pattern) => pattern.test(text))) {
    return false;
  }

  const operations = [
    /\b(arruma|arrume|corrige|corrija|conserta|conserte|repara|repare)\b/,
    /\b(verifica|verifique|confere|confira|testa|teste)\b/,
    /\b(implementa|implemente|cria|crie|adiciona|adicione|remove|remova)\b/,
    /\b(atualiza|atualize|muda|mude|ajusta|ajuste|melhora|melhore)\b/,
    /\b(investiga|investigue|diagnostica|diagnostique)\b/,
    /\b(fix|repair|correct|verify|check|test|implement|create|add|remove)\b/,
    /\b(update|change|adjust|improve|deploy|publish|sync|inspect|diagnose)\b/,
  ];

  const scopes = [
    /\b(sistema|system|worker|runtime|repo|repository|projeto|project)\b/,
    /\b(codigo|code|pagina|page|landing|pricing|checkout|stripe|supabase)\b/,
    /\b(database|banco|api|github|cloudflare|frontend|backend|ui)\b/,
    /\b(chat|conversation|mission|missao|deploy|production|producao)\b/,
  ];

  const contextual = [
    /\b(agora|depois|tambem|tb|also|then|next|resto|remaining)\b/,
    /\b(continua|continue|faz o mesmo|do the same|corrige o que faltar)\b/,
  ];

  const operation = operations.some((pattern) => pattern.test(text));
  const scope = scopes.some((pattern) => pattern.test(text));
  const continuation = contextual.some((pattern) => pattern.test(text));

  return operation && (scope || continuation || text.length >= 12);
}

export function detectCyanTakeoverIntent(message: string): CyanTakeoverIntent {
  const text = normalized(message);

  if (!text) {
    return {
      type: "NONE",
    };
  }

  const prepareSignals = [
    "analyze this project",
    "analyse this project",
    "analyze the project",
    "analyse the project",
    "diagnose this project",
    "diagnose the project",
    "prepare takeover",
    "prepare everything",
    "analyze everything",
    "analyse everything",
    "analise este projeto",
    "analise o projeto",
    "diagnostique este projeto",
    "prepare tudo",
    "prepare o takeover",
  ];

  if (prepareSignals.some((signal) => text.includes(signal))) {
    return {
      type: "PREPARE",

      objective: message.trim(),
    };
  }

  const activateSignals = [
    "execute the plan",
    "start execution",
    "activate takeover",
    "go ahead",
    "you can execute",
    "pode executar",
    "pode comecar",
    "pode começar",
    "execute o plano",
    "inicie a execucao",
    "inicie a execução",
  ];

  if (activateSignals.some((signal) => text.includes(signal))) {
    return {
      type: "ACTIVATE",
    };
  }

  const refreshSignals = [
    "update status",
    "refresh status",
    "show progress",
    "where are we",
    "status do projeto",
    "atualize o status",
    "mostre o progresso",
    "como esta o projeto",
    "como está o projeto",
  ];

  if (refreshSignals.some((signal) => text.includes(signal))) {
    return {
      type: "REFRESH",
    };
  }

  const appendSignals = [
    "add this to the queue",
    "add to the queue",
    "add this idea",
    "include this too",
    "adicione isso a fila",
    "adicione isso à fila",
    "adicione esta ideia",
    "inclua isso tambem",
    "inclua isso também",
  ];

  if (appendSignals.some((signal) => text.includes(signal))) {
    return {
      type: "APPEND",

      objective: message.trim(),
    };
  }

  if (isNaturalOperationalMission(message)) {
    return {
      type: "PREPARE",
      objective: message.trim(),
    };
  }

  return {
    type: "NONE",
  };
}

/**
 * Natural owner confirmations are activation signals only when the
 * caller has already established an activatable prepared operational
 * session. This helper MUST NOT be used as a global intent detector.
 */
export function isCyanOwnerActivationConfirmation(message: string): boolean {
  const normalized = message
    .trim()
    .toLocaleLowerCase()
    .replace(/[.!?]+$/g, "")
    .trim();

  const exactConfirmations = new Set([
    "yes",
    "yes proceed",
    "yes, proceed",
    "proceed",
    "execute",
    "go ahead",
    "confirm",
    "confirmed",
    "approved",
    "sim",
    "sim pode executar",
    "sim, pode executar",
    "pode executar",
    "pode prosseguir",
    "confirme",
    "confirmado",
    "aprovado",
  ]);

  return exactConfirmations.has(normalized);
}
